import { OutputMigration, UOMS } from '@amzn/awscat-portfolio-assessment-types';
import { Grid } from '@amzn/awsui-components-react';
import { Layout, PieData } from 'plotly.js';
import PlotlyBasic from 'plotly.js-basic-dist';
import { createRef, useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';

import { constants } from '../../common/Localization';
import { formatCurrencyAsString } from '../../common/LocalizationHelper';
import { useResize } from '../../common/resize-hook';
import { uomConversion } from '../../common/uomConversion';
import { commonGraphData, legendLayout, margins } from './constants';

const PlotlyChart = createPlotlyComponent(PlotlyBasic);

interface GraphData {
    partnerPCT: number;
    awsPCT: number;
    customerPCT: number;
    toolPCT: number;
    partnerPCTResource: number;
    awsPCTResource: number;
    customerPCTResource: number;
    partnerPCTResourceCount: number;
    awsPCTResourceCount: number;
    customerPCTResourceCount: number;
    costHoverText: string[];
    countHoverText: string[];
}

interface Props {
    migration: OutputMigration,
    currency: string,
}

function MigrationSummaryCostGraphs(props: Props): JSX.Element {
    const componentRef = createRef<HTMLDivElement>();
    const graphDimensions = useResize(componentRef);
    const graphWidth = graphDimensions.width / 4;

    const [ graphData, setGraphData ] = useState<GraphData>();
    
    useEffect(() => {
        const convertToPercentageAndRound = (value: number): number => {
            return Math.round(uomConversion(value, UOMS.DECIMAL, UOMS.PERCENTAGE));
        };
        const calculateGraphData = (): void => {
            const { currency, migration } = props;
            const totalResourceCost = migration.totalResourceCosts[0];
            const totalMigrationCost = migration.totalMigrationCost;

            const partnerPCT = convertToPercentageAndRound(totalResourceCost.partner / totalMigrationCost);
            const customerPCT = convertToPercentageAndRound(totalResourceCost.customer/ totalMigrationCost);
            const awsPCT = convertToPercentageAndRound(totalResourceCost.aws / totalMigrationCost);
            const totalToolsCost = migration.tools.discoveryTool + migration.tools.migrationTool 
                + migration.tools.trainingCost + (migration.tools.otherCosts || 0);
            const toolPCT = convertToPercentageAndRound(totalToolsCost / totalMigrationCost);

            const totalResourceCostPCT = totalResourceCost.partner + totalResourceCost.customer + totalResourceCost.aws;
            const partnerPCTResource = convertToPercentageAndRound(totalResourceCost.partner / totalResourceCostPCT);
            const customerPCTResource = convertToPercentageAndRound(totalResourceCost.customer / totalResourceCostPCT);
            const awsPCTResource = convertToPercentageAndRound(totalResourceCost.aws / totalResourceCostPCT);

            const resourceCountTotals = migration.sprints[migration.sprints.length - 1];
            const partnerResourceCount = resourceCountTotals.totalPartnerResources;
            const customerResourceCount = resourceCountTotals.totalCustomerResources;
            const awsResourceCount = resourceCountTotals.totalAwsResources;
            const totalResourceCountPCT = partnerResourceCount + customerResourceCount + awsResourceCount;

            const partnerPCTResourceCount = convertToPercentageAndRound(partnerResourceCount / totalResourceCountPCT);
            const customerPCTResourceCount = convertToPercentageAndRound(customerResourceCount / totalResourceCountPCT);
            const awsPCTResourceCount = convertToPercentageAndRound(awsResourceCount / totalResourceCountPCT);

            const costHoverValues = [totalResourceCost.partner, totalResourceCost.aws, totalResourceCost.customer, totalToolsCost];
            const costHoverText = costHoverValues.map(text => 
                formatCurrencyAsString(text, {currency, maximumFractionDigits: 0, minimumFractionDigits: 0}).toString());
            const countHoverText = [partnerResourceCount.toString(), awsResourceCount.toString(), customerResourceCount.toString()];
            setGraphData( {
                partnerPCT,
                customerPCT,
                awsPCT,
                toolPCT,
                partnerPCTResource,
                customerPCTResource,
                awsPCTResource,
                partnerPCTResourceCount,
                customerPCTResourceCount,
                awsPCTResourceCount,
                costHoverText,
                countHoverText,
            });
        };
        calculateGraphData();
    }, [props]);

    const commonLayout: Partial<Layout> = {
        width: graphWidth,
        showlegend: true,
        legend: legendLayout,
    };

    const totalCostChart = (): JSX.Element | null => {
        if (!graphData) {
            return null;
        }
        const layout: Partial<Layout> = {
            ...commonLayout,
            title: constants.TOTAL_COST_DISTRIBUTION,
            margin: {...margins, l: 20},
        };
    
        const pieData: Partial<PieData>[] = [
            {
                ...commonGraphData,
                values: [graphData.partnerPCT, graphData.awsPCT, graphData.customerPCT, graphData.toolPCT],
                hovertext: graphData.costHoverText,
            }
        ];

        return (
            <PlotlyChart data={pieData} layout={layout}/>
        );
    };

    const resourceCostChart = (): JSX.Element | null => {
        if (!graphData) {
            return null;
        }

        const layout: Partial<Layout> = {
            ...commonLayout,
            title: constants.RESOURCE_COST_DISTRIBUTION,
            margin: margins,
        };
    
        const pieData: Partial<PieData>[] = [
            {
                ...commonGraphData,
                values: [graphData.partnerPCTResource, graphData.awsPCTResource, graphData.customerPCTResource],
                hovertext: graphData.costHoverText,
            }
        ];
        return (
            <PlotlyChart data={pieData} layout={layout}/>
        );
    };

    const resourceCountChart = (): JSX.Element | null => {
        if (!graphData) {
            return null;
        }

        const layout: Partial<Layout> = {
            ...commonLayout,
            title: constants.RESOURCE_COUNT_DISTRIBUTION,
            margin: margins,
        };
    
        const pieData: Partial<PieData>[] = [
            {
                ...commonGraphData,
                values: [graphData.partnerPCTResourceCount, graphData.awsPCTResourceCount, graphData.customerPCTResourceCount],
                hovertext: graphData.countHoverText,
            }
        ];
        return (
            <PlotlyChart data={pieData} layout={layout}/>
        );
    };
    
    return (
        <div ref={componentRef}>
            <Grid gridDefinition={[{colspan: 4}, {colspan: 4}, {colspan: 4}]}>
                {totalCostChart()}
                {resourceCostChart()}
                {resourceCountChart()}
            </Grid>
        </div>
    );
}

export default MigrationSummaryCostGraphs;
